import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SuiNotificationPopover, SuiNotification } from '@splashthat/sui';
import { useGatekeeperPing } from './hooks/useGatekeeperPing';
import { useGetHeartbeatId } from './hooks/useGetHeartbeatId';
import { GATEKEEPER_STATES } from './constants';

const GatekeeperHeartbeatManagerContext = React.createContext({
    status: GATEKEEPER_STATES.NOT_INITIALIZED,
});

const GatekeeperHeartbeatManager = ({
    enabled,
    guestAccessHash,
    guestId,
    eventId,
    onGateClosed,
    onGateOpened,
    errorMessage,
    children,
}) => {
    const [gateStatus, setGateStatus] = useState(GATEKEEPER_STATES.NOT_INITIALIZED);

    const { heartbeatId, heartbeatGateStatus, timeToLeave } = useGetHeartbeatId({
        enabled,
        guestAccessHash,
        guestId,
        eventId,
    });

    const isPingEnabled = GATEKEEPER_STATES.OPENED === heartbeatGateStatus && enabled;
    const REFRESH_PERCENTAGE_THRESHOLD = 0.3;
    const refetchInterval = (timeToLeave - timeToLeave * REFRESH_PERCENTAGE_THRESHOLD) * 1000;

    const { pingGateStatus } = useGatekeeperPing({
        heartbeatId,
        guestAccessHash,
        enabled: isPingEnabled,
        refetchInterval,
    });

    useEffect(() => {
        const isGateClosed = [heartbeatGateStatus, pingGateStatus].includes(GATEKEEPER_STATES.CLOSED);
        const isGateOpened = [heartbeatGateStatus, pingGateStatus].every(
            (status) => status === GATEKEEPER_STATES.OPENED,
        );

        if (isGateClosed) {
            setGateStatus(GATEKEEPER_STATES.CLOSED);
            onGateClosed();
        } else if (isGateOpened) {
            setGateStatus(GATEKEEPER_STATES.OPENED);
            onGateOpened();
        }
    }, [heartbeatGateStatus, pingGateStatus]);

    return (
        <GatekeeperHeartbeatManagerContext.Provider value={{ heartbeatId, gateStatus, errorMessage }}>
            <SuiNotificationPopover>
                <SuiNotification
                    message={errorMessage}
                    variation="ERROR"
                    active={gateStatus === GATEKEEPER_STATES.CLOSED}
                />
            </SuiNotificationPopover>
            {children}
        </GatekeeperHeartbeatManagerContext.Provider>
    );
};

GatekeeperHeartbeatManager.propTypes = {
    enabled: PropTypes.bool.isRequired,
    guestAccessHash: PropTypes.string.isRequired,
    guestId: PropTypes.string.isRequired,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    errorMessage: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    onGateClosed: PropTypes.func,
    onGateOpened: PropTypes.func,
};

GatekeeperHeartbeatManager.defaultProps = {
    onGateClosed: () => {},
    onGateOpened: () => {},
};

export const useGatekeeperHeartbeatManager = () => React.useContext(GatekeeperHeartbeatManagerContext);

export default GatekeeperHeartbeatManager;
